import { X } from 'lucide-react';
import { useState, type ChangeEvent, type KeyboardEvent } from 'react';
import styles from '../SegmentVisualBuilder.module.css';
import type { BuilderConditionValue } from '../segment-builder.types';

interface TokenizedValueInputProps {
  value: BuilderConditionValue[];
  onChange: (value: BuilderConditionValue[]) => void;
  disabled?: boolean;
}

function parseValues(rawValue: string): string[] {
  return rawValue
    .split(',')
    .map(token => token.trim())
    .filter(Boolean);
}

/** Token text for display, comparison, and accessible labels; non-string values keep their stored type. */
function tokenLabel(value: BuilderConditionValue): string {
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function TokenizedValueInput({ value, onChange, disabled }: TokenizedValueInputProps) {
  const [inputValue, setInputValue] = useState('');

  const addValues = (rawValue: string) => {
    const existingLabels = new Set(value.map(tokenLabel));
    const newValues: string[] = [];
    for (const parsed of parseValues(rawValue)) {
      if (existingLabels.has(parsed)) continue;
      existingLabels.add(parsed);
      newValues.push(parsed);
    }
    if (newValues.length > 0) onChange([...value, ...newValues]);
  };

  const commitInputValue = () => {
    addValues(inputValue);
    setInputValue('');
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextInputValue = event.target.value;
    if (nextInputValue.includes(',')) {
      const inputParts = nextInputValue.split(',');
      const pendingValue = inputParts.pop() ?? '';
      addValues(inputParts.join(','));
      setInputValue(pendingValue);
    } else {
      setInputValue(nextInputValue);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitInputValue();
    }
  };

  const removeValueAt = (indexToRemove: number) => {
    onChange(value.filter((_, index) => index !== indexToRemove));
  };

  return (
    <div className={styles.tokenInput}>
      {value.map((item, index) => {
        const label = tokenLabel(item);
        return (
          <span key={`${label}-${index}`} className={styles.token}>
            {label}
            <button
              type="button"
              className={styles.tokenRemoveButton}
              onClick={() => removeValueAt(index)}
              disabled={disabled}
              aria-label={`Remove ${label}`}>
              <X size={14} aria-hidden />
            </button>
          </span>
        );
      })}
      <input
        type="text"
        className={styles.tokenInputField}
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder={value.length === 0 ? 'Type a value, then press Enter' : 'Add another'}
        aria-label="Values"
      />
    </div>
  );
}
